/*
 * The articles page's and the release-note list's own headings, from the
 * Settings page since 2026-10-15: each a title - the page's h1, the first half of
 * its browser tab and the link back to it from every item - and the line under
 * it, which may be empty (none).
 *
 * Each falls back when the site row has not got its fields: the bootstrap is
 * cached for a minute and more, so for a moment after the deploy that added them
 * a reader's new script can meet the old shape, and a page must not read
 * "undefined" (the review of 2026-10-15; the theme guards the same way in
 * SiteBranding). The articles title falls back to the SITE title, which was that
 * page's heading until then - so the old shape shows exactly what it showed.
 *
 * IN THE READER'S LANGUAGE (2026-10-29, S49). Each heading can be written in
 * Danish too, on the same Settings card. A Danish reader gets the Danish half by
 * half - title and line apart - and the English for any half not written, which
 * is null on the row; a bootstrap cached from before the Danish fields existed
 * has them undefined, and gets the English the same way.
 */

import {
  DEFAULT_RELEASE_NOTES_SUBTITLE,
  DEFAULT_RELEASE_NOTES_TITLE,
  type PublicSiteSettings,
} from '@accotool-kb/shared';
import type { LanguageCode } from '../i18n/language.js';

export interface PageHeading {
  title: string;
  /** Empty means no line under the heading. */
  subtitle: string;
}

/** A Danish half where the reader is Danish and one is written, else the English. */
function inLanguage(english: PageHeading, language: LanguageCode, title?: string | null, subtitle?: string | null): PageHeading {
  if (language !== 'da-dk') return english;
  return { title: title ?? english.title, subtitle: subtitle ?? english.subtitle };
}

/** The articles page - the browse page at `/` - and the link back to it. */
export function articlesHeading(site: PublicSiteSettings, language: LanguageCode = 'en-us'): PageHeading {
  return inLanguage(
    { title: site.articlesTitle ?? site.title, subtitle: site.subtitle },
    language,
    site.articlesTitleDa,
    site.articlesSubtitleDa,
  );
}

/** The release-note list at `/releasenotes`, and the link back to it. */
export function releaseNotesHeading(site: PublicSiteSettings, language: LanguageCode = 'en-us'): PageHeading {
  return inLanguage(
    {
      title: site.releaseNotesTitle ?? DEFAULT_RELEASE_NOTES_TITLE,
      subtitle: site.releaseNotesSubtitle ?? DEFAULT_RELEASE_NOTES_SUBTITLE,
    },
    language,
    site.releaseNotesTitleDa,
    site.releaseNotesSubtitleDa,
  );
}

/**
 * A list page's browser tab: its own title, then the site's - unless the two are
 * the same words, when once is enough (the articles title starts as the site
 * title).
 */
export function listPageTabTitle(pageTitle: string, siteTitle: string): string {
  return pageTitle === siteTitle ? siteTitle : `${pageTitle} · ${siteTitle}`;
}
